import MuiButton from '@mui/material/Button';
import MuiDrawer from '@mui/material/Drawer';
import { useAuthentication } from '@src/authentication/useAuthentication';
import { Avatar } from '@src/common/components/Avatar';
import { Logo } from '@src/common/components/brand/Logo';
import { ChangeLocaleButton } from '@src/common/components/ChangeLocaleButton';
import { HorizontalDivider } from '@src/common/components/HorizontalDivider';
import { IconButton } from '@src/common/components/IconButton';
import { Popover } from '@src/common/components/Popover';
import { Stack } from '@src/common/components/Stack';
import { StatusBadge } from '@src/common/components/StatusBadge';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import DevFlags, { DevFlag } from '@src/helpers/DevFlags';
import { permitStatusColors } from '@src/orders/components/OrderStatusColors';
import { LogOut01 as LogOutIcon } from '@untitled-ui/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';

export const SideNav = ({
  children,
  additionalSettings,
}: {
  children: ReactNode;
  additionalSettings?: ReactNode;
}) => {
  const { user, logout } = useAuthentication();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <MuiDrawer
      classes={{ root: sideNav, paper: content }}
      variant="permanent"
      anchor="left"
    >
      <Stack flex={1} gap="10px">
        <Stack
          alignItems="center"
          padding={theme.spacing['2']}
          paddingBottom={theme.spacing['4']}
        >
          <Logo height={50} width={50} />
          {DevFlags.isEnabled(DevFlag.DEV_WATERMARK) && (
            <StatusBadge
              size="small"
              text="Dev"
              color={permitStatusColors['ISSUED'].color}
              borderColor={permitStatusColors['ISSUED'].border}
              backgroundColor={permitStatusColors['ISSUED'].background}
            />
          )}
        </Stack>

        {children}
      </Stack>
      <HorizontalDivider />

      <Stack alignItems="center">
        <ChangeLocaleButton />
      </Stack>

      {additionalSettings}

      {user && (
        <Stack
          direction="column"
          alignItems="center"
          textAlign="center"
          gap={theme.spacing['2']}
        >
          <Popover
            openPopoverComponent={
              <div aria-label={user.name} className={avatar}>
                <Avatar />
              </div>
            }
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
            openPopoverProps={{ className: openPopoverStyle }}
          >
            <Stack
              direction="column"
              textAlign="center"
              width="fit-content"
              spacing={theme.spacing['4']}
              margin={theme.spacing['2']}
            >
              <Typography style={{ fontWeight: 600 }}>{user.name}</Typography>
              <HorizontalDivider />
              <IconButton
                className={iconBtnStyle}
                title={t('logout')}
                onClick={() => {
                  navigate('/');
                  logout();
                }}
              >
                <LogOutIcon width={24} height={24} />
                <Typography style={{ fontWeight: 500 }}>
                  {t('logout')}
                </Typography>
              </IconButton>
            </Stack>
          </Popover>
        </Stack>
      )}
    </MuiDrawer>
  );
};

const avatar = css({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#DDE3E7',
    borderRadius: '100px',
  },
});

const iconBtnStyle = css({
  gap: theme.spacing['2'],
  justifyContent: 'flex-start',
  color: theme.palette.surface.onSurface,
  borderRadius: '8px',
});

const openPopoverStyle = css({
  transition: 'none',
  '&:hover': {
    backgroundColor: 'unset',
  },
});

const sideNav = css({
  width: 100,
});

const content = css({
  backgroundColor: theme.palette.surface.surfaceContainerLowest,
  padding: `${theme.spacing['5']} ${theme.spacing['1']}`,
  width: 100,
  gap: theme.spacing['5'],
});

export const SideNavLink = ({
  to,
  children,
  icon,
}: {
  to: string;
  children: ReactNode;
  icon: ReactNode;
}) => {
  return (
    <Stack alignItems="center" padding={theme.spacing['1']}>
      <Stack>
        <MuiButton
          style={{ width: '65px' }}
          component={NavLink}
          to={to}
          classes={{ root: sideNavLink, icon: navLinkIcon }}
          startIcon={icon}
        ></MuiButton>
      </Stack>
      <MuiButton
        component={NavLink}
        to={to}
        classes={{ root: sideNavLinkText }}
        disableRipple
      >
        {children}
      </MuiButton>
    </Stack>
  );
};

const sideNavLink = css({
  color: theme.palette.surface.onSurface,
  padding: '6px 10px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  transition: 'none',
  '&.active': {
    backgroundColor: theme.palette.secondary.secondaryContainer,
    borderRadius: '100px',
  },
  '&:hover': {
    backgroundColor: theme.palette.secondary.secondaryContainer,
    borderRadius: '100px',
  },
});

const sideNavLinkText = css({
  color: theme.palette.surface.onSurface,
  padding: '0 10px',
  display: 'flex',
  flexDirection: 'column',
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: theme.spacing['6'],
  letterSpacing: '0.5px',
  textAlign: 'center',
  '&.active': {
    backgroundColor: 'unset',
    fontWeight: 600,
  },
  '&:hover': {
    backgroundColor: 'unset',
  },
});

const navLinkIcon = css({
  margin: 0,
});
